import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "range", "onChange", "minDate", "maxDate", "onFocus", "disabled", "onKeyDown", "onClick", "disableDates", "calendarProps", "locale", "className", "calendarRef", "type", "wrapperProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { forwardRef, useEffect, useRef } from 'react';
import { cnBem, combineRefs } from '@carnica/utils';
import { useFlag, useIsMobileDimension } from '@carnica/utils/hooks';
import styles from "./styles.pcss";
import { Input } from "./Input";
import { Calendar } from "./Calendar";
import { CalendarWrapper } from "./CalendarWrapper";
import { CloseButton } from "./CloseButton";
var cnDatePicker = cnBem('date-picker', styles);
var getValidDate = function getValidDate(a, b) {
  if (!a || !b) {
    return [a, b];
  }
  return b < a ? [b, a] : [a, b];
};
var DatePickerRender = function DatePickerRender(props, ref) {
  var value = props.value,
    range = props.range,
    onChange = props.onChange,
    minDateProp = props.minDate,
    maxDateProp = props.maxDate,
    onFocus = props.onFocus,
    disabled = props.disabled,
    onKeyDown = props.onKeyDown,
    onClick = props.onClick,
    disableDates = props.disableDates,
    calendarProps = props.calendarProps,
    locale = props.locale,
    className = props.className,
    calendarRef = props.calendarRef,
    _props$type = props.type,
    type = _props$type === void 0 ? 'date' : _props$type,
    wrapperProps = props.wrapperProps,
    otherProps = _objectWithoutProperties(props, _excluded);
  var _useFlag = useFlag(),
    _useFlag2 = _slicedToArray(_useFlag, 2),
    isCalendarOpen = _useFlag2[0],
    setIsCalendarOpen = _useFlag2[1];
  var _getValidDate = getValidDate(minDateProp, maxDateProp),
    _getValidDate2 = _slicedToArray(_getValidDate, 2),
    minDate = _getValidDate2[0],
    maxDate = _getValidDate2[1];
  var inputRef = useRef(null);
  var wrapperRef = useRef(null);
  var handleFocus = function handleFocus() {
    if (!disabled) {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      onFocus === null || onFocus === void 0 ? void 0 : onFocus.apply(void 0, args);
      setIsCalendarOpen.on();
    }
  };
  var handleClick = function handleClick() {
    if (!disabled) {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }
      onClick === null || onClick === void 0 ? void 0 : onClick.apply(void 0, args);
      setIsCalendarOpen.on();
    }
  };
  var isMobile = useIsMobileDimension();
  var onCalendarChange = function onCalendarChange() {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    onChange === null || onChange === void 0 ? void 0 : onChange.apply(void 0, args);
    setIsCalendarOpen.off();
  };
  var handleKeyDown = function handleKeyDown(e) {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    if (e.key === 'Tab') {
      var _wrapperRef$current;
      (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : _wrapperRef$current.focus();
    }
  };
  var onCompleteKeyDown = function onCompleteKeyDown(e) {
    if (e.code === 'Escape' || e.code === 'Tab') {
      setIsCalendarOpen.off();
    }
  };
  var onWrapperClick = function onWrapperClick(e) {
    if (!disabled) {
      var _wrapperProps$onClick;
      wrapperProps === null || wrapperProps === void 0 ? void 0 : (_wrapperProps$onClick = wrapperProps.onClick) === null || _wrapperProps$onClick === void 0 ? void 0 : _wrapperProps$onClick.call(wrapperProps, e);
      setIsCalendarOpen.on();
    }
  };
  useEffect(function () {
    if (disabled) {
      setIsCalendarOpen.off();
    }
  }, [disabled]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Input, _extends({}, otherProps, {
    className: cnDatePicker(null, [className]),
    disableDates: disableDates,
    disabled: disabled,
    maxDate: maxDate,
    minDate: minDate,
    onChange: onChange,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    range: range,
    ref: combineRefs([inputRef, ref]),
    type: type,
    value: value,
    wrapperProps: _objectSpread(_objectSpread({}, wrapperProps || {}), {}, {
      onClick: onWrapperClick
    }),
    "data-t-id": "components-DatePicker"
  })), /*#__PURE__*/React.createElement(CalendarWrapper, {
    anchorRef: inputRef,
    className: cnDatePicker('wrapper'),
    isOpen: isCalendarOpen,
    onClose: setIsCalendarOpen.off
  }, /*#__PURE__*/React.createElement(Calendar, _extends({}, calendarProps || {}, {
    additionalControls: isMobile ? undefined : /*#__PURE__*/React.createElement(CloseButton, {
      onClick: setIsCalendarOpen.off
    }),
    className: cnDatePicker('calendar', [calendarProps === null || calendarProps === void 0 ? void 0 : calendarProps.className]),
    completeButtonProps: {
      onKeyDown: onCompleteKeyDown
    },
    disableDates: disableDates,
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    onChange: onCalendarChange,
    range: range,
    ref: combineRefs([calendarRef, wrapperRef]),
    type: type,
    value: value,
    "data-t-id": "components-DatePicker"
  })), isMobile && /*#__PURE__*/React.createElement(CloseButton, {
    onClick: setIsCalendarOpen.off
  })));
};
export var DatePicker = /*#__PURE__*/forwardRef(DatePickerRender);